import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  Guitar, Sparkles, Palette, Wine, Play, MapPin, Clock, Calendar, ArrowRight, Check,
} from "lucide-react";
import { IMAGES } from "@/lib/constants";
import { MaskLine, Reveal, Badge, GlitchDivider } from "@/components/site/primitives";
import Countdown from "@/components/site/Countdown";
import { api } from "@/lib/api";

const HIGHLIGHTS = [
  { icon: Guitar, title: "Live Spider-Punk", body: "Jole & DJ Ciphers detonate an electric Hobie-Brown set of afrobeat, hip-hop & multiverse glitch.", img: IMAGES.spiderPunk, color: "#ff0055", span: "md:col-span-7", tag: "HEADLINER" },
  { icon: Sparkles, title: "Suit Up Your Way", body: "Multiverse cosplay runway & costume contest across five wild categories.", img: IMAGES.cosplay, color: "#00f0ff", span: "md:col-span-5", tag: "RUNWAY" },
  { icon: Palette, title: "RiNo Comic Alley", body: "Live graffiti mural battles, print artists & comic creators lining the ArtPark.", img: IMAGES.graffiti, color: "#ffe600", span: "md:col-span-5", tag: "ART" },
  { icon: Wine, title: "Craft Mocktail Bar", body: "100% non-alcoholic signature mocktails plus a roster of local Denver food trucks.", img: IMAGES.mocktail, color: "#00f0ff", span: "md:col-span-7", tag: "ALL-AGES" },
];

const INSTA = [IMAGES.cosplay, IMAGES.streetwear, IMAGES.spiderPunk, IMAGES.cosplayHero, IMAGES.graffiti, IMAGES.concert];

const TICKET_TIERS = [
  { name: "General Admission", price: "30", color: "#00f0ff", perks: ["Full event access", "All stage shows & runway", "Comic Alley & mocktail bar"] },
  { name: "VIP Spider-Society", price: "65", color: "#ff0055", featured: true, perks: ["Early 3PM entry", "Signed holographic art print", "Mezzanine VIP lounge + 2 mocktail tokens"] },
  { name: "Family 4-Pack", price: "100", color: "#ffe600", perks: ["Entry for 4 guests", "Saves $20", "Kids 3 & under free"] },
];

export default function Home() {
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.type === "eventism-widget-resize") {
        document.querySelectorAll('iframe[src*="hev_22eb140f9a9d"]').forEach((f) => {
          f.style.height = e.data.height + "px";
        });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const trackTicket = () => {
    api.post("/track", { type: "ticket_click" }).catch(() => {});
    navigate("/tickets");
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroDenver} alt="Denver neon skyline" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] via-[#08080c]/70 to-[#08080c]/40" />
          <div className="absolute inset-0 halftone-magenta opacity-30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 md:px-8 pb-16 md:pb-24 pt-32">
          <div className="reveal-anim flex flex-wrap gap-2 mb-6">
            <Badge color="yellow">Sat · Sept 19 · 2026</Badge>
            <Badge color="cyan">4PM – 10PM</Badge>
            <Badge color="magenta">All-Ages</Badge>
          </div>

          <h1 className="font-display text-[15vw] leading-[0.82] md:text-[8.5vw] lg:text-[7.5vw] uppercase max-w-6xl">
            <MaskLine><span className="text-white">Anyone Can</span></MaskLine>
            <MaskLine><span className="chromatic">Wear The Mask.</span></MaskLine>
            <MaskLine><span className="text-[#ffe600]">Enter The Denver</span></MaskLine>
            <MaskLine><span className="text-[#00f0ff]">Spider-Verse.</span></MaskLine>
          </h1>

          <p className="reveal-anim mt-6 max-w-2xl text-white/75 font-head text-base md:text-lg" style={{ animationDelay: "0.15s" }}>
            Denver's premier all-ages multiverse art, music &amp; cosplay experience. Live streetwear runway,
            breakdance ciphers, comic alley &amp; non-alcoholic craft mocktails.
          </p>

          <div className="reveal-anim mt-8 flex flex-col sm:flex-row gap-4" style={{ animationDelay: "0.25s" }}>
            <button data-testid="hero-get-tickets" onClick={trackTicket} className="inline-flex items-center justify-center gap-2 bg-[#ff0055] text-white font-head font-bold uppercase tracking-wide px-8 py-4 rounded-full glow-magenta hover:scale-105 active:scale-95 transition-transform">
              Get Tickets <span className="text-white/70 text-xs font-mono2">· via Eventism</span>
            </button>
            <Link data-testid="hero-join-runway" to="/entertainment#runway" className="inline-flex items-center justify-center gap-2 glass-chroma text-white font-head font-bold uppercase tracking-wide px-8 py-4 rounded-full hover:scale-105 transition-transform">
              Join Cosplay Runway <ArrowRight size={18} />
            </Link>
          </div>

          <div className="reveal-anim mt-10" style={{ animationDelay: "0.35s" }}>
            <div className="font-mono2 text-[10px] tracking-[0.3em] text-white/50 uppercase mb-3">Multiverse collapses in</div>
            <Countdown compact />
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="py-6 bg-[#101018] border-y border-white/10 overflow-hidden">
        <Marquee speed={40} gradient={false}>
          {["SUIT UP YOUR WAY", "THWIP!", "LIVE SPIDER-PUNK", "COMIC ALLEY", "ALL-AGES", "MULTIVERSE RUNWAY", "RINO ARTPARK"].map((t, i) => (
            <span key={i} className="font-display text-5xl md:text-7xl mx-8 text-outline">{t}</span>
          ))}
        </Marquee>
      </div>

      {/* HIGHLIGHTS BENTO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="font-mono2 text-xs tracking-[0.3em] text-[#ff0055] uppercase mb-3">01 — The Experience</div>
              <h2 className="font-display text-5xl md:text-7xl uppercase">Six Hours,<br /><span className="text-[#00f0ff]">Infinite Dimensions</span></h2>
            </div>
            <p className="text-white/60 font-head max-w-sm">One historic warehouse. Endless universes colliding across music, art, fashion &amp; flavor.</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.title} delay={i * 0.08} className={h.span}>
              <div data-testid={`highlight-card-${i}`} className="group relative h-72 md:h-80 rounded-2xl overflow-hidden glass-chroma glitch-hover">
                <img src={h.img} alt={h.title} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] via-[#08080c]/50 to-transparent" />
                <div className="relative h-full p-6 flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-full grid place-items-center border border-white/20" style={{ background: `${h.color}22` }}>
                      <h.icon size={22} style={{ color: h.color }} />
                    </div>
                    <span className="font-mono2 text-[10px] tracking-[0.25em] px-2 py-1 border border-white/20 rounded-full text-white/70">{h.tag}</span>
                  </div>
                  <div>
                    <h3 className="font-display text-3xl md:text-4xl uppercase" style={{ color: h.color }}>{h.title}</h3>
                    <p className="text-white/70 font-head text-sm mt-2 max-w-md">{h.body}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <GlitchDivider />

      {/* VIDEO REEL */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden glass-chroma aspect-video grid place-items-center">
            <img src={IMAGES.concert} alt="Event reel" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 halftone opacity-30" />
            <button data-testid="video-reel-play" className="relative z-10 group flex flex-col items-center gap-4">
              <span className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-[#ff0055] grid place-items-center glow-magenta group-hover:scale-110 transition-transform">
                <Play size={36} className="text-white ml-1" fill="white" />
              </span>
              <span className="font-display text-3xl md:text-5xl uppercase chromatic">Watch The Teaser</span>
            </button>
          </div>
        </Reveal>
      </section>

      {/* EVENT DETAILS STRIP */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-10">
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { icon: Calendar, label: "When", value: "Sat, Sept 19 2026 · 4–10PM MDT" },
            { icon: MapPin, label: "Where", value: "RiNo ArtPark & Truss House, Denver CO" },
            { icon: Clock, label: "VIP Early Entry", value: "Doors 3:00 PM for Spider-Society" },
          ].map((d, i) => (
            <Reveal key={d.label} delay={i * 0.08}>
              <div className="glass rounded-xl p-6 flex items-center gap-4">
                <d.icon className="text-[#00f0ff]" />
                <div>
                  <div className="font-mono2 text-[10px] tracking-[0.25em] text-white/50 uppercase">{d.label}</div>
                  <div className="font-head font-semibold">{d.value}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INSTAGRAM WALL */}
      <section className="py-20 md:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="font-mono2 text-xs tracking-[0.3em] text-[#ffe600] uppercase mb-3">02 — Community</div>
            <h2 className="font-display text-5xl md:text-7xl uppercase mb-12">From The <span className="text-[#ff0055]">Multiverse</span></h2>
          </Reveal>
          <div className="flex flex-wrap justify-center gap-6">
            {INSTA.map((src, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  style={{ transform: `rotate(${(i % 2 === 0 ? -1 : 1) * (3 + (i % 3))}deg)` }}
                  className="polaroid w-44 h-56 md:w-56 md:h-72 bg-white p-2 pb-8 shadow-[6px_6px_0_rgba(0,0,0,0.6)] rounded-sm"
                >
                  <img src={src} alt="Community cosplay" className="w-full h-full object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TICKETS — works like the ticketing page */}
      <section id="tickets" className="max-w-7xl mx-auto px-5 md:px-8 pb-24 scroll-mt-24">
        <Reveal>
          <div className="glass-chroma rounded-3xl p-8 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 halftone-magenta opacity-20" />
            <div className="relative text-center">
              <Badge color="magenta">Powered by Eventism</Badge>
              <h2 className="font-display text-5xl md:text-8xl uppercase mt-4">Your Universe<br /><span className="chromatic">Is Calling.</span></h2>
              <p className="text-white/60 font-head max-w-xl mx-auto mt-4">Grab your passes below — secure checkout handled by Eventism. Kids 3 &amp; under free.</p>
            </div>

            <div className="relative grid md:grid-cols-3 gap-5 mt-10">
              {TICKET_TIERS.map((t) => (
                <div key={t.name} data-testid={`home-tier-${t.name.split(" ")[0].toLowerCase()}`} className={`rounded-2xl p-6 ${t.featured ? "glass-chroma chromatic-box" : "glass"}`}>
                  <h3 className="font-display text-2xl uppercase" style={{ color: t.color }}>{t.name}</h3>
                  <div className="font-display text-5xl mt-2">${t.price}<span className="text-white/40 text-lg">.00</span></div>
                  <ul className="mt-4 space-y-2">
                    {t.perks.map((p) => (
                      <li key={p} className="flex gap-2 text-sm text-white/80 font-head"><Check size={16} style={{ color: t.color }} className="shrink-0 mt-0.5" /> {p}</li>
                    ))}
                  </ul>
                  <a href="#home-eventism" onClick={() => api.post("/track", { type: "ticket_click" }).catch(() => {})} data-testid={`home-buy-${t.name.split(" ")[0].toLowerCase()}`} className="mt-5 block text-center font-head font-bold uppercase py-3 rounded-full transition-transform hover:scale-[1.03]" style={{ background: t.color, color: t.featured ? "#fff" : "#000" }}>Buy on Eventism</a>
                </div>
              ))}
            </div>

            <div id="home-eventism" className="relative mt-10 flex flex-col items-center scroll-mt-24">
              <div id="eventism-ticketing-widget" data-event-id="spider-verse-denver-2026" className="w-full max-w-[600px] glass rounded-2xl p-4 md:p-6 flex justify-center">
                <iframe
                  data-testid="home-eventism-iframe"
                  title="Get Tickets - Into the Spider-Verse Denver"
                  src="https://eventism.com/embed/tickets/hev_22eb140f9a9d?accent=ff0055&bg=101018&text=ffffff&btnText=ffffff&showImage=true"
                  style={{ width: "100%", maxWidth: "560px", minHeight: "620px", border: "none", borderRadius: "14px" }}
                  allow="payment"
                />
              </div>
              <button data-testid="cta-get-tickets" onClick={trackTicket} className="mt-6 inline-flex items-center gap-2 glass-chroma text-white font-head font-bold uppercase tracking-wide px-8 py-3.5 rounded-full hover:scale-105 transition-transform">
                See full ticket details <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
